// Shared email validation for the Troopr auth screens.
//
// Each domain label must be non-empty and dot-free, so "company..com"
// (empty label) is rejected. The old /[^\s@]+\.[^\s@]+/ let the pre-dot
// segment swallow a dot, which passed every consecutive-dot domain.
export const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

// RFC 5321 limits: 254 for the whole address, 64 for the local part, 255 for
// the domain. The regex alone is unbounded, so without these a 300-character
// local part validates and we queue a send that can never be delivered.
export const MAX_EMAIL_LENGTH = 254;
export const MAX_LOCAL_PART_LENGTH = 64;
export const MAX_DOMAIN_LENGTH = 255;

// Live length feedback for the auth screens: the message to show under the
// input, or null when the address is within limits. Length only — it stays
// quiet about shape so the user isn't told "invalid email" halfway through
// typing one. Full validation still runs on submit via isValidEmail.
export function emailLengthError(email) {
  const trimmed = String(email || '').trim();
  if (!trimmed) return null;
  // Name before "@" first: when it is what makes the address too long, the
  // total-length message would not tell the user which part to shorten.
  const atIndex = trimmed.lastIndexOf('@');
  if (atIndex > MAX_LOCAL_PART_LENGTH) {
    return `Name before "@" too long (max ${MAX_LOCAL_PART_LENGTH} characters)`;
  }
  if (trimmed.length > MAX_EMAIL_LENGTH) {
    return `Email too long (max ${MAX_EMAIL_LENGTH} characters)`;
  }
  if (atIndex >= 0 && trimmed.length - atIndex - 1 > MAX_DOMAIN_LENGTH) {
    return `Domain too long (max ${MAX_DOMAIN_LENGTH} characters)`;
  }
  return null;
}

export function isValidEmail(email) {
  if (!email || typeof email !== 'string') return false;
  const trimmed = email.trim();
  if (trimmed.length > MAX_EMAIL_LENGTH) return false;
  if (!EMAIL_RE.test(trimmed)) return false;
  // lastIndexOf: the regex already bars '@' in the domain, but this keeps the
  // split correct for anything that slips past it.
  const atIndex = trimmed.lastIndexOf('@');
  if (atIndex <= 0 || atIndex === trimmed.length - 1) return false;
  if (atIndex > MAX_LOCAL_PART_LENGTH) return false;
  if (trimmed.length - atIndex - 1 > MAX_DOMAIN_LENGTH) return false;
  return true;
}
